import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { api } from '@/shared/lib/api';
import { cls } from '@/shared/lib/classes';
import {
  buscarTelas,
  cotaDoCaminho,
  favoritosDoMenu,
  type ItemDeTela,
  moduloDoCaminho,
  modulosVisiveis,
  secoesDoMenu,
} from '@/shared/lib/cotas';
import {
  encerrarSessaoLocal,
  podeFazer,
  useSemEmpresaVinculada,
  useSessao,
} from '@/shared/lib/sessao';
import { Aviso } from '@/shared/ui/Aviso';
import { Marca, MarcaDeRegistro, NomeDaMarca } from '@/shared/ui/Marca';
import { Versao } from '@/shared/ui/Versao';
import { MenuDeFavoritos, SecaoDoMenu } from './menu/SecaoDoMenu';
import { type MenuPersonalizado, useMenuPersonalizado } from './menu/useMenuPersonalizado';
import { queryClient } from './query-client';

/**
 * Moldura da aplicação: faixa de identificação no topo, painel de navegação
 * escuro à esquerda e área de trabalho clara.
 *
 * O contraste entre os dois lados não é decorativo — separa "onde eu estou" de
 * "o que eu estou fazendo" sem precisar de borda, e a área de trabalho fica com
 * o fundo mais legível sob sol (.spec/014, seção 6).
 *
 * **O menu é retrátil em qualquer largura.** No celular ele é uma gaveta sobre
 * o conteúdo; no desktop, uma coluna que sai do fluxo quando o usuário fecha.
 * O motivo é o mesmo dos dois lados: as telas de lançamento e as listagens
 * largas competem por largura com um menu que, depois de aprendido, quase não
 * se olha. Quem precisa dele traz de volta com um clique — e a escolha fica
 * gravada, porque preferência de espaço de trabalho não se refaz a cada login.
 *
 * **Quem são as telas** está em `shared/lib/cotas.ts`, indexadas pela cota. Aqui
 * ficou só o desenho: este arquivo não sabe rótulo, rota nem permissão de
 * ninguém, e acrescentar uma tela ao menu é acrescentar uma cota lá.
 *
 * **O que está dentro de cada seção** — painel e submenus — e o menu de
 * Favoritos, acima delas, são `menu/SecaoDoMenu.tsx`; o que é do usuário
 * (favoritos, modo de organizar, submenus abertos) é
 * `menu/useMenuPersonalizado.ts` (`.spec/015`, decisões 75 e 105).
 */

/** Altura da faixa do topo. Presa aqui porque o menu se ancora abaixo dela. */
const ALTURA_TOPO = 'h-16';

const CHAVE_MODULOS = 'construtora.menu.modulos';
const CHAVE_MENU_ABERTO = 'construtora.menu.aberto';

/** A partir daqui cabe menu e conteúdo lado a lado. Igual ao `lg` do Tailwind. */
const CONSULTA_TELA_LARGA = '(min-width: 1024px)';

const ICONE_SAIR = (
  <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M6 14H2.5v-12H6M10 11l3-3-3-3M13 8H6" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const ICONE_AJUDA = (
  <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M6.2 6.3a1.9 1.9 0 0 1 3.7.5c0 1.3-1.9 1.6-1.9 2.9"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path d="M8 11.2v.6" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

/**
 * Endereço do manual, já na seção da tela aberta.
 *
 * `index.html` explícito, e não `/ajuda/`: o `try_files {path} /index.html` do
 * Caddy acha o arquivo pelo nome, e o endereço não depende de como o servidor
 * resolve diretório — no pior caso cairia no SPA, e o usuário veria o sistema
 * em vez da ajuda. A âncora troca o ponto médio por hífen (`CMP·03` → `#CMP-03`),
 * que é como o gerador nomeia as seções.
 */
function enderecoDaAjuda(cota: string | null): string {
  return `/ajuda/index.html${cota ? `#${cota.replace('·', '-')}` : ''}`;
}

/** Chave do `localStorage` que a ajuda lê (`infra/scripts/ajuda/ajuda.js`). */
const CHAVE_ACESSO_DA_AJUDA = 'obranalinha:ajuda-acesso';

/**
 * Grava as telas que o usuário enxerga, para a ajuda mostrar só os capítulos delas.
 *
 * **Por que no clique, e não a ajuda perguntando à API.** A sessão vive na
 * memória desta aba, e a ajuda é página estática em outra aba: para descobrir o
 * papel ela teria de renovar a sessão, e a renovação gira o token de refresh —
 * a aba do sistema ficaria com o token velho e cairia no próximo pedido.
 *
 * **Não é controle de acesso, é arrumação.** O manual não tem dado de cliente e
 * o arquivo é público dentro do domínio; quem bloqueia a tela é a API. Por isso
 * o recorte vem do mesmo `modulosVisiveis` do menu, e falhar ao gravar
 * (aba anônima, armazenamento cheio) só faz a ajuda mostrar o manual inteiro.
 */
function gravarAcessoDaAjuda(): void {
  const cotas = modulosVisiveis(podeFazer).flatMap((modulo) =>
    modulo.itens.map((item) => item.cota),
  );
  try {
    localStorage.setItem(CHAVE_ACESSO_DA_AJUDA, JSON.stringify({ cotas, em: Date.now() }));
  } catch {
    // Sem gravar, a ajuda abre completa. Tira o recorte antigo, se houver: ele
    // pode ser de outro usuário que usou este navegador.
    try {
      localStorage.removeItem(CHAVE_ACESSO_DA_AJUDA);
    } catch {
      // Armazenamento indisponível de vez: não há recorte a tirar.
    }
  }
}

const ICONE_BUSCA = (
  <svg
    className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-grafite-500"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="7" cy="7" r="4.25" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

/**
 * Hamburguer e "X" no mesmo botão.
 *
 * Um botão só, que troca de desenho, em vez de dois que aparecem e somem: o
 * alvo não se move, então fechar é clicar de novo onde acabou de se clicar.
 */
function IconeMenu({ aberto }: { aberto: boolean }): React.ReactElement {
  return (
    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      {aberto ? (
        <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.75" />
      ) : (
        <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.75" />
      )}
    </svg>
  );
}

/**
 * O que a busca devolve, em lista corrida.
 *
 * Sem as seções, de propósito: quem digitou já disse o que procura, e reagrupar
 * seis resultados em quatro títulos faria percorrer hierarquia para chegar ao
 * que está à vista. O módulo vira legenda embaixo do nome — é o que responde
 * "por que esta tela apareceu" quando o que casou foi ele.
 *
 * A cota aparece à direita, e não é enfeite: ver o código ao lado do nome é como
 * o usuário aprende a citá-lo no chamado seguinte.
 */
function ResultadosDaBusca({
  resultados,
  aoEscolher,
}: {
  resultados: ItemDeTela[];
  aoEscolher: () => void;
}): React.ReactElement {
  if (resultados.length === 0) {
    return (
      <p className="px-3 py-6 text-sm text-grafite-400">
        Nenhuma tela com esse nome ou cota. Procure pelo assunto — "estoque", "obra" — ou pelo
        código do cabeçalho.
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      {resultados.map((item) => (
        <Link
          key={item.cota}
          to={item.para}
          onClick={aoEscolher}
          className="flex min-h-toque items-center gap-3 border-l-2 border-l-transparent px-3 text-sm font-medium text-grafite-300 transition-colors hover:bg-grafite-800 hover:text-papel-100"
        >
          <span className="flex min-w-0 flex-col py-1.5 leading-tight">
            <span className="truncate">{item.rotulo}</span>
            <span className="cota truncate text-grafite-500">{item.modulo}</span>
          </span>
          <span className="cota ml-auto shrink-0 text-grafite-500">{item.cota}</span>
        </Link>
      ))}
    </div>
  );
}

/**
 * A linha abaixo da busca: o botão que liga o modo de organizar e o recado de
 * falha ao gravar.
 *
 * **Um modo, e não a estrela sempre à vista.** A estrela ao passar o mouse não
 * existe no celular, e a estrela fixa em cada linha seria ruído na coluna que
 * se olha cem vezes por dia para usar uma vez por mês. O modo deixa o gesto
 * explícito — "estou arrumando o menu" — e é nele que as setas de ordem
 * aparecem.
 */
function BarraDeOrganizar({ menu }: { menu: MenuPersonalizado }): React.ReactElement {
  return (
    <div className="flex flex-col gap-1 px-2 pt-2">
      <button
        type="button"
        onClick={menu.alternarOrganizacao}
        aria-pressed={menu.organizando}
        className={cls(
          'cota flex min-h-toque items-center gap-2 px-3 text-left transition-colors lg:min-h-0 lg:py-1.5',
          menu.organizando
            ? 'bg-zarcao-500 text-grafite-900'
            : 'text-grafite-500 hover:text-papel-100',
        )}
      >
        <span aria-hidden="true">★</span>
        {menu.organizando ? 'Concluir' : 'Organizar favoritos'}
      </button>
      {menu.organizando ? (
        <p className="px-3 text-xs leading-snug text-grafite-400">
          A estrela põe a tela no menu Favoritos, no topo; as setas mudam a ordem dela lá. Vale em
          qualquer computador em que você entrar.
        </p>
      ) : null}
      {menu.falhouAoGravar ? (
        <p role="status" className="px-3 text-xs leading-snug text-atencao-50">
          Não foi possível gravar os favoritos. O menu mostra o que ficou salvo.
        </p>
      ) : null}
    </div>
  );
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  const primeira = partes[0]?.[0] ?? '?';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';

  return (primeira + ultima).toUpperCase();
}

/**
 * Leitura tolerante do `localStorage`.
 *
 * Devolve `null` em qualquer imprevisto — chave ausente, JSON quebrado, aba
 * anônima que proíbe o acesso. Preferência de menu não vale uma tela branca,
 * então o erro vira "sem preferência gravada" e o padrão assume.
 */
function lerModulosGravados(): string[] | null {
  try {
    const bruto = localStorage.getItem(CHAVE_MODULOS);
    if (bruto === null) return null;

    const valor: unknown = JSON.parse(bruto);
    if (!Array.isArray(valor)) return null;

    return valor.filter((item): item is string => typeof item === 'string');
  } catch {
    return null;
  }
}

function gravar(chave: string, valor: unknown): void {
  try {
    localStorage.setItem(chave, JSON.stringify(valor));
  } catch {
    // Sem preferência gravada o sistema continua inteiro; falhar aqui em voz
    // alta seria pior que esquecer.
  }
}

/**
 * Acompanha a largura da janela.
 *
 * O CSS sozinho não resolve: a mesma variável decide se o menu nasce aberto,
 * se o clique num item deve fechá-lo e se existe fundo escurecido atrás. Fazer
 * isso com classe de breakpoint exigiria manter dois comportamentos em
 * paralelo, e um deles ficaria errado.
 */
function useTelaLarga(): boolean {
  const [larga, setLarga] = useState(() => window.matchMedia(CONSULTA_TELA_LARGA).matches);

  useEffect(() => {
    const consulta = window.matchMedia(CONSULTA_TELA_LARGA);
    const aoMudar = (evento: MediaQueryListEvent): void => {
      setLarga(evento.matches);
    };

    consulta.addEventListener('change', aoMudar);
    return () => {
      consulta.removeEventListener('change', aoMudar);
    };
  }, []);

  return larga;
}

/**
 * Aviso de usuário sem empresa vinculada.
 *
 * Fica no shell, e não em cada tela, por dois motivos. O primeiro é alcance: os
 * campos de empresa, obra, depósito e insumo aparecem em dezenas de telas, e a
 * versão por tela seria a que esquecesse a próxima. O segundo é a natureza do
 * problema — não é esta tela que está errada, é o usuário que não foi
 * configurado, e o recado vale enquanto isso não mudar.
 *
 * A alternativa descartada foi deixar os seletores vazios. Vazio é indistinguível
 * de cadastro vazio, e é exatamente o defeito que o sistema inteiro passou a
 * evitar quando a leitura dos catálogos deixou de depender de papel: lista sem
 * opção manda o usuário procurar um cadastro que existe.
 */
/**
 * O segundo fator que o papel exige e ainda não foi ativado.
 *
 * No shell pelo mesmo motivo do aviso de empresa: não é a tela que está errada,
 * é a credencial que está incompleta, e o recado tem de acompanhar a pessoa
 * até ela resolver. Some sozinho na sessão seguinte à ativação — o login é
 * quem calcula `mfaPendente`.
 */
function AvisoMfaPendente(): React.ReactElement | null {
  const sessao = useSessao();
  if (!sessao?.mfaPendente) return null;

  return (
    <Aviso tom="atencao" titulo="Ative a verificação em duas etapas" className="mb-6">
      Seu papel de administrador exige um segundo fator no login, e ele ainda não está ativo. Leva
      um minuto:{' '}
      <Link to="/minha-conta" className="font-medium underline underline-offset-2">
        ativar agora em Minha conta
      </Link>
      .
    </Aviso>
  );
}

function AvisoSemEmpresa(): React.ReactElement | null {
  const semVinculo = useSemEmpresaVinculada();
  if (!semVinculo) return null;

  return (
    <Aviso tom="atencao" titulo="Usuário sem empresa vinculada" className="mb-6">
      Enquanto seu usuário não estiver vinculado a uma empresa do grupo, os campos de empresa, obra,
      depósito e insumo vão aparecer sem opções — não é falta de cadastro. Peça a um administrador
      para fazer o vínculo no cadastro de usuários.
    </Aviso>
  );
}

export function AppShell(): React.ReactElement {
  const sessao = useSessao();
  const navigate = useNavigate();
  const caminho = useRouterState({ select: (estado) => estado.location.pathname });
  const [saindo, setSaindo] = useState(false);
  const telaLarga = useTelaLarga();
  // Uma cota só, a mais específica: quem decide o destaque é o catálogo, para o
  // menu não ter uma segunda opinião sobre onde o usuário está.
  const cotaAberta = cotaDoCaminho(caminho);
  const menu = useMenuPersonalizado(cotaAberta);

  /*
    O termo da busca mora aqui, e não no `localStorage`: procurar tela é ato de
    um instante, e reabrir o sistema com o filtro de ontem escondendo o menu
    seria preferência que ninguém pediu para gravar.
  */
  const [busca, setBusca] = useState('');

  /*
    No celular o menu nasce fechado, sempre: ali ele cobre o conteúdo, e abrir
    uma tela já com o conteúdo tapado inverte a ordem das coisas. No desktop
    vale a última escolha do usuário, e na falta dela, aberto.
  */
  const [menuAberto, setMenuAberto] = useState(() => {
    if (!window.matchMedia(CONSULTA_TELA_LARGA).matches) return false;

    return localStorage.getItem(CHAVE_MENU_ABERTO) !== 'false';
  });

  /*
    Módulos abertos. Sem preferência gravada, abre só o da tela atual: o menu
    começa curto e o usuário vê, pelo que está aberto, onde ele está.
  */
  const [modulosAbertos, setModulosAbertos] = useState<string[]>(() => {
    const gravados = lerModulosGravados();
    if (gravados !== null) return gravados;

    const atual = moduloDoCaminho(window.location.pathname);
    return atual === null ? [] : [atual];
  });

  const fecharSeEstreito = useCallback((): void => {
    if (!window.matchMedia(CONSULTA_TELA_LARGA).matches) setMenuAberto(false);
  }, []);

  /*
    Escolher um resultado limpa a busca. Voltar ao menu de sempre é o estado em
    que o usuário quer encontrar a coluna da próxima vez que olhar para ela — e
    deixar o termo aceso esconderia o item aberto do próprio menu.
  */
  const aoEscolherResultado = useCallback((): void => {
    setBusca('');
    fecharSeEstreito();
  }, [fecharSeEstreito]);

  /*
    A sessão caiu sem o usuário pedir — sai da tela.

    A guarda de rota (`beforeLoad`) só roda ao **navegar**, e quem perde a sessão
    no meio de uma consulta não está navegando: ficaria parado numa tela que não
    consegue mais buscar nada, com o cabeçalho ainda mostrando quem era. Este
    efeito é a metade reativa da guarda.

    O cache vai junto, pelo mesmo motivo do `sair()`: ele vive na memória da aba
    e não na sessão, então quem entrar depois herdaria as respostas de quem saiu.
    Isso importa mais aqui do que na saída voluntária — a causa mais provável de
    a sessão cair sozinha é justamente **outra conta** ter assumido o navegador.

    `replace`, e não `push`: a tela de onde se saiu não existe mais para esta
    sessão, e deixá-la no histórico convidaria o Voltar a esbarrar de novo na
    guarda.
  */
  useEffect(() => {
    if (sessao) return;

    queryClient.clear();
    void navigate({ to: '/login', replace: true });
  }, [sessao, navigate]);

  // Ao estreitar a janela o menu vira gaveta, e gaveta aberta tapa a tela;
  // ao alargar, volta a caber ao lado do conteúdo.
  useEffect(() => {
    setMenuAberto(telaLarga ? localStorage.getItem(CHAVE_MENU_ABERTO) !== 'false' : false);
  }, [telaLarga]);

  // Esc fecha a gaveta. Só no estreito: no desktop o menu não é modal, e Esc
  // ali pertence ao diálogo ou ao campo que estiver em foco.
  useEffect(() => {
    if (telaLarga || !menuAberto) return;

    const aoTeclar = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape') setMenuAberto(false);
    };

    window.addEventListener('keydown', aoTeclar);
    return () => {
      window.removeEventListener('keydown', aoTeclar);
    };
  }, [telaLarga, menuAberto]);

  function alternarMenu(): void {
    setMenuAberto((aberto) => {
      // Só a escolha feita no desktop é lembrada: o fechamento automático da
      // gaveta do celular não é preferência, é consequência de navegar.
      if (telaLarga) gravar(CHAVE_MENU_ABERTO, !aberto);

      return !aberto;
    });
  }

  function alternarModulo(titulo: string): void {
    setModulosAbertos((abertos) => {
      const proximos = abertos.includes(titulo)
        ? abertos.filter((item) => item !== titulo)
        : [...abertos, titulo];

      gravar(CHAVE_MODULOS, proximos);
      return proximos;
    });
  }

  /*
    Esc dentro do campo limpa o termo antes de fechar a gaveta, e por isso
    interrompe o evento: as duas coisas de uma vez fariam o usuário perder a
    busca e o menu com uma tecla só, sem ter pedido nem uma nem outra.
  */
  function aoTeclarNaBusca(evento: React.KeyboardEvent<HTMLInputElement>): void {
    if (evento.key === 'Escape' && busca !== '') {
      evento.stopPropagation();
      setBusca('');
    }
  }

  async function sair(): Promise<void> {
    setSaindo(true);
    try {
      await api.post('/api/v1/auth/logout', {});
    } finally {
      // Mesmo com falha de rede o cliente esquece a sessão: deixar a tela
      // aberta depois de o usuário pedir para sair é pior que uma sessão órfã
      // no servidor, que expira sozinha.
      encerrarSessaoLocal();

      /*
        O cache de consultas vai junto, e isto **não** é limpeza cosmética.

        O `queryClient` vive na memória da aba, não na sessão: sem o `clear()`,
        quem entra depois herda as respostas de quem saiu. Em duas contas
        diferentes na mesma aba isso é vazamento entre tenants na tela — a
        listagem do segundo usuário mostra os registros do primeiro até cada
        consulta revalidar sozinha.

        E é o que fazia "sair e entrar de novo" não resolver nada: o estado do
        catálogo NCM tem `staleTime` de cinco minutos, então a tela repetia a
        resposta antiga — "catálogo não importado" — depois de o operador já ter
        importado.
      */
      queryClient.clear();

      await navigate({ to: '/login', replace: true });
    }
  }

  const procurando = busca.trim() !== '';
  const resultados = buscarTelas(busca, podeFazer);
  const secoesVisiveis = secoesDoMenu(podeFazer);
  const favoritos = favoritosDoMenu(podeFazer, menu.favoritos);

  return (
    <div className="min-h-screen bg-papel-100">
      <header
        className={cls(
          'sticky top-0 z-40 flex items-center gap-3 border-b border-grafite-800 bg-grafite-900 px-3 text-papel-200 sm:px-4',
          ALTURA_TOPO,
        )}
      >
        <button
          type="button"
          onClick={alternarMenu}
          aria-expanded={menuAberto}
          aria-controls="menu-modulos"
          aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
          className="grid h-11 w-11 shrink-0 place-items-center text-papel-200 transition-colors hover:bg-grafite-800 hover:text-papel-50"
        >
          <IconeMenu aberto={menuAberto} />
        </button>

        <div className="flex min-w-0 items-center gap-3">
          <Marca className="h-7 w-7 shrink-0 text-zarcao-500" classeDaLinha="text-papel-50" />
          <div className="min-w-0 leading-none">
            <NomeDaMarca tom="escuro" className="block truncate text-lg" />
            <p className="cota mt-1 hidden text-grafite-400 sm:block">Gestão de obras</p>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          {/*
            A ajuda abre na tela em que o usuário está: a cota é a âncora do
            manual (`#CMP-03`). Em aba nova, para não perder o formulário que
            está aberto — e porque a ajuda é página estática, fora do SPA
            (`apps/web/public/ajuda`, gerada de `ajuda/*.md` por `pnpm ajuda`).
          */}
          <a
            href={enderecoDaAjuda(cotaAberta)}
            onClick={gravarAcessoDaAjuda}
            onAuxClick={gravarAcessoDaAjuda}
            target="_blank"
            rel="noopener noreferrer"
            title={cotaAberta ? `Ajuda da tela ${cotaAberta}` : 'Manual do sistema'}
            className="flex min-h-toque items-center gap-2 px-2 text-sm text-grafite-300 transition-colors hover:text-papel-50"
          >
            {ICONE_AJUDA}
            <span className="hidden sm:inline">Ajuda</span>
          </a>

          {sessao ? (
            <div className="flex min-w-0 items-center gap-2">
              {/* Texto grafite sobre o laranja cheio, nunca branco: branco ali
                  dá 2,4:1 (regra 2 do LEIA-ME do kit da marca). */}
              <span
                aria-hidden="true"
                className="grid h-9 w-9 shrink-0 place-items-center bg-zarcao-500 font-mono text-xs font-semibold text-grafite-900"
              >
                {iniciais(sessao.nome)}
              </span>
              {/* O nome some no estreito e as iniciais ficam: o quadrado
                  laranja já responde "que conta é esta" no espaço que há. */}
              {/* O nome é o atalho para "Minha conta": senha e segundo fator
                  moram lá, e é onde todo mundo procura o próprio acesso. */}
              <Link
                to="/minha-conta"
                className="hidden min-w-0 leading-tight md:block"
                title="Minha conta"
              >
                <p className="truncate text-sm font-medium text-papel-100 hover:text-zarcao-300">
                  {sessao.nome}
                </p>
                <p className="cota truncate text-grafite-400">{sessao.papeis.join(' · ')}</p>
              </Link>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => void sair()}
            disabled={saindo}
            className="flex min-h-toque items-center gap-2 px-2 text-sm text-grafite-300 transition-colors hover:text-papel-50 disabled:opacity-50"
          >
            {ICONE_SAIR}
            <span className="hidden sm:inline">Sair</span>
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Fundo escurecido da gaveta. Só existe no estreito, onde o menu
            cobre o conteúdo; clicar nele fecha, como em qualquer gaveta. */}
        {menuAberto && !telaLarga ? (
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => {
              setMenuAberto(false);
            }}
            className="fixed inset-0 z-30 cursor-default bg-grafite-950/60"
          />
        ) : null}

        {menuAberto ? (
          <aside
            id="menu-modulos"
            className={cls(
              'fixed inset-y-0 left-0 z-40 flex w-72 max-w-[85vw] animate-deslizar flex-col overflow-y-auto bg-grafite-900 pb-6 text-papel-200',
              // No desktop deixa de ser gaveta: entra no fluxo, ao lado do
              // conteúdo, e acompanha a rolagem colado abaixo da faixa.
              'lg:sticky lg:top-16 lg:z-auto lg:h-[calc(100vh-4rem)] lg:max-w-none lg:animate-none',
              // Organizando, a coluna alarga: estrela e duas setas numa linha
              // de 15rem deixariam o rótulo em meia palavra, e é pelo rótulo
              // que se decide o que marcar. Volta ao normal ao concluir.
              menu.organizando ? 'lg:w-80' : 'lg:w-60',
            )}
          >
            <MarcaDeRegistro className="absolute right-2 top-2 hidden text-papel-300 lg:block" />

            {/* Cabeçalho da gaveta: no estreito ela cobre a faixa do topo, e
                sem isto o usuário perde de vista o botão que a fechou. */}
            <div
              className={cls(
                'flex items-center justify-between border-b border-grafite-800 px-3 lg:hidden',
                ALTURA_TOPO,
              )}
            >
              <span className="cota text-grafite-400">Módulos</span>
              <button
                type="button"
                onClick={() => {
                  setMenuAberto(false);
                }}
                aria-label="Fechar menu"
                className="grid h-11 w-11 place-items-center text-papel-200 transition-colors hover:bg-grafite-800 hover:text-papel-50"
              >
                <IconeMenu aberto />
              </button>
            </div>

            {/*
              A busca fica acima das seções e sempre visível.

              Ela existe porque o menu passou de trinta telas em oito módulos: a
              partir daí, quem sabe o que quer paga o pedágio de lembrar em qual
              seção aquilo mora. Aceita o nome e a cota — a cota é como o suporte
              se refere à tela ao telefone, e digitá-la leva direto lá.
            */}
            <div className="px-2 pt-3">
              <div className="relative">
                {ICONE_BUSCA}
                <input
                  type="search"
                  value={busca}
                  onChange={(evento) => {
                    setBusca(evento.target.value);
                  }}
                  onKeyDown={aoTeclarNaBusca}
                  aria-label="Buscar tela por nome ou cota"
                  placeholder="Buscar tela ou cota"
                  autoComplete="off"
                  className="min-h-toque w-full border border-grafite-800 bg-grafite-950 py-2 pl-9 pr-3 text-sm text-papel-100 placeholder:text-grafite-500 focus:border-zarcao-500 focus:outline-none lg:min-h-0"
                />
              </div>
            </div>

            {procurando ? null : <BarraDeOrganizar menu={menu} />}

            <nav
              aria-label={procurando ? 'Resultados da busca' : 'Módulos'}
              className="flex flex-col gap-1 px-2 pt-3"
            >
              {procurando ? (
                <ResultadosDaBusca resultados={resultados} aoEscolher={aoEscolherResultado} />
              ) : (
                <>
                  <MenuDeFavoritos
                    itens={favoritos}
                    cotaAberta={cotaAberta}
                    aoNavegar={fecharSeEstreito}
                    menu={menu}
                  />
                  {secoesVisiveis.map((secao, indice) => (
                    <SecaoDoMenu
                      key={secao.titulo}
                      secao={secao}
                      // Pelo índice, e não pelo título: "Administração" viraria um
                      // `id` com acento, que é válido mas quebra seletor de CSS e
                      // de teste sem escape.
                      indice={indice}
                      aberta={modulosAbertos.includes(secao.titulo)}
                      aoAlternar={() => {
                        alternarModulo(secao.titulo);
                      }}
                      cotaAberta={cotaAberta}
                      aoNavegar={fecharSeEstreito}
                      menu={menu}
                    />
                  ))}
                </>
              )}
            </nav>

            <div className="mt-auto hidden px-5 pt-4 lg:block">
              <Versao />
            </div>
          </aside>
        ) : null}

        <div className="min-w-0 flex-1">
          <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 lg:px-10">
            <AvisoMfaPendente />
            <AvisoSemEmpresa />
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
